/**
 * The #51 infinite query (§5.4, FEED_SPEC).
 *
 * Keyed on the **full** param set — scope, filters, range, sort — so any change starts a new query
 * rather than replaying a cursor cut under a different ordering or a different author set. That is
 * the whole reason a keyset feed is safe here: a cursor never outlives the query it belongs to.
 */

import { useInfiniteQuery, type UseInfiniteQueryResult, type InfiniteData } from '@tanstack/react-query';
import { MAX_FEED_LIMIT, type GlobalFeedQuery, type GlobalFeedResponse } from '@lw/shared';
import { getGlobalFeed } from '../../api/feed';
import { QK } from '../../api/queryKeys';

export function useFeedQuery(
  params: Partial<GlobalFeedQuery>,
  enabled: boolean,
): UseInfiniteQueryResult<InfiniteData<GlobalFeedResponse>, Error> {
  const base: Partial<GlobalFeedQuery> = { ...params, limit: MAX_FEED_LIMIT };
  return useInfiniteQuery({
    queryKey: QK.feedPage(base),
    queryFn: ({ pageParam, signal }) =>
      getGlobalFeed({ ...base, cursor: pageParam ?? undefined }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  });
}
